/**
 * Client-side prediction.
 *
 * Runs `@chaos/sim`'s `stepWorld` locally, immediately, on the LOCAL PLAYER,
 * so pressing forward moves you this frame instead of one round-trip later.
 * Same code as the server; the only difference is who is allowed to be right.
 *
 * ── What is predicted, and what is not ────────────────────────────────────
 *
 * PREDICTED: position, velocity, facing, grounded, jump/coyote/buffer state, the
 * dash, and every travel an ability puts your own body through (the roll, the
 * dash, the leap, your half of the ride). These are functions of your own
 * input, the map, and the state the server tells you you are in — so the
 * server will agree.
 *
 * NOT PREDICTED: health, damage, death, kills, ult charge, and other players'
 * DECISIONS. Those depend on eight players' inputs and the server's ordering,
 * so guessing them produces a wrong number on screen that has to be visibly
 * corrected — and in a wagered match "your health bar lied to you" is not an
 * acceptable UX bug.
 *
 * ── What the server knows about you, you must know too ────────────────────
 *
 * Two things the local world did not hold made most of the teleports the
 * founder saw (measured in `scripts/teleport-harness.mjs`):
 *
 *   OTHER BODIES. The world had one player in it. NVDA's roll stops at the
 *   first body on the server and ran its full 3 m here; separation, the dash
 *   through a target and the ride all mispredicted the same way — 22 hard
 *   snaps in two minutes at 120 ms. The other players are now IN the world,
 *   as bodies: placed from the newest snapshot, dead-reckoned by their own
 *   velocity for the ticks this world is ahead of it, with no input and no
 *   health that can run out. They are obstacles the local body meets exactly
 *   where the server will meet them, give or take their last few frames of
 *   motion — never opponents whose intent is guessed.
 *
 *   YOUR OWN EFFECTS AND CASTS. A stun, a slow, a Gravity Well, a Robotaxi
 *   ride are put on you by someone else, and until now the snapshot named
 *   them without their numbers, so the predictor ran on without them — at
 *   full speed while the server held you. The wire's own row now carries them
 *   in full (`fx`), plus your pending casts (`casts`), so a snapshot mid-dash
 *   restores the dash and the replay finishes it. See `applySnapshot`.
 *
 * PREDICTED (since the chaos wire fix): the director's mechanical mutations.
 * Low gravity, ice, conveyors and speed surges are global physics, not another
 * player's hidden input — refusing to predict them meant every step of a
 * two-minute mutator diverged, and the reconciler replayed each correction
 * through the same unmutated physics, so the player stuttered for the whole
 * duration. `syncChaos` folds the snapshot's authoritative ledger in;
 * `applyChaosEvent` applies the landing the moment the event arrives.
 */

import {
  createWorld,
  createFlatMap,
  stepWorld,
  applyMutation,
  isMutation,
  msToTicks,
  reloadFraction,
  createAux,
  findTravel,
  isFlight,
  DECOY_ID_PREFIX,
  DT,
} from '@chaos/sim';
import { CHARACTERS } from '@chaos/protocol';

/** The snapshot carries the reload as a fraction; the sim counts it in ticks. */
function reloadTicksFrom(player, fraction) {
  const ammo = CHARACTERS[player.characterId]?.abilities.basic.ammo;
  if (!ammo) return 0;
  return Math.round(Math.max(0, Math.min(1, fraction ?? 0)) * msToTicks(ammo.reloadMs));
}

/**
 * Per-tick decay of the render-only correction offset, and the magnitude below
 * which it snaps to zero. 0.65^tick puts a 1m correction under 2cm in ~9 ticks
 * (~300ms) — long enough that a Seismic Slam knockback reads as being THROWN
 * rather than teleported, short enough that the drawn body never lags the
 * authoritative one by a reactable amount.
 */
const CORRECTION_DECAY = 0.65;
const CORRECTION_FLOOR = 0.02;

/**
 * How far ahead of its snapshot a remote body is dead-reckoned, in ticks.
 *
 * The local world runs `unacked` ticks ahead of the newest snapshot — the
 * round trip, roughly — and a body that kept walking is that far from where
 * the snapshot drew it. Past this many ticks the guess is worse than holding,
 * so a stalled link holds.
 */
const REMOTE_LEAD_MAX_TICKS = 8;

/**
 * A remote body's health in the local world. Never a number that can reach
 * zero: a locally predicted basic that "killed" an opponent would award the
 * local player a bounty, a heal and a cooldown refund the server never gave.
 */
const REMOTE_HEALTH = 1e9;

export class Predictor {
  /**
   * @param {object} opts
   * @param {string} opts.playerId   server-side user id (NOT the sessionId)
   * @param {string} opts.characterId
   * @param {object} opts.map        MapDef from @chaos/sim
   * @param {string} opts.seed       any stable string; the local world's RNG is
   *                                 never consulted for anything that matters
   */
  constructor({ playerId, characterId, map, seed = 'client-prediction' }) {
    this.playerId = playerId;
    this.map = map ?? createFlatMap();
    this.world = createWorld({
      seed,
      map: this.map,
      players: [{ id: playerId, characterId }],
      // Loot is the server's. Crates drawn from this placeholder seed would be
      // lockboxes only this client can see — bullets stopping on nothing, and
      // pickups granting HP the snapshot then takes back.
      loot: false,
    });
    this.player = this.world.players[0];

    // The zone is the server's business. A predicted zone would tick damage on a
    // client whose zone centre came from a different RNG stream than the
    // server's, and predicted damage is exactly what this module refuses to do.
    //
    // `startRadius` is the lever, not `radius` or `dps`: `stepZone` recomputes
    // both of those from the tick on every single step, so setting them here
    // would last exactly one tick. An effectively infinite start radius means
    // nobody is ever outside the circle and the damage loop never fires.
    this.world.zone.startRadius = Number.MAX_SAFE_INTEGER;
    this.world.zone.radius = Number.MAX_SAFE_INTEGER;
    this.world.zone.dps = 0;

    this._inputs = new Map();
    /** Position at the start of the current tick, for render interpolation. */
    this.prev = { x: this.player.x, y: this.player.y, z: this.player.z, yaw: this.player.yaw };
    this.curr = { ...this.prev };

    /**
     * Render-only correction offset, prev/curr pairs like the body so the same
     * `alpha` drives both. When the reconciler snaps a divergence too large for
     * input replay to explain (a knockback, a Gravity Well pull), the drawn
     * body starts from where it WAS on screen and bleeds onto the authoritative
     * position over a few ticks — the simulation, hit tests and every consumer
     * of `this.player` see only the snapped truth. Decayed per tick rather than
     * per wall-clock read: this module steps a deterministic world and keeps
     * clocks out entirely.
     */
    this._smoothPrev = { x: 0, y: 0, z: 0 };
    this._smoothCurr = { x: 0, y: 0, z: 0 };
    /** Last output of `interpolate` — the position actually on screen. */
    this._lastRender = null;

    /** Last authoritative non-positional state, re-applied after every step. */
    this._auth = {
      health: this.player.health,
      phase: 'alive',
      ultCharge: 0,
    };

    /**
     * The other bodies: id -> the SimPlayer standing in for them in this
     * world, plus the newest snapshot pose to dead-reckon from. See the header.
     */
    this._remote = new Map();

    // The floor-mutator overlay reads its chaos ledger straight off this world
    // — the predictor is the only structured view of "which mutations are live"
    // a production client has. Handed over through a global rather than an
    // import: ArenaRenderer already discovers its world source by probing
    // globals (the gym/debug handles), and importing it here would pull the
    // whole three/webgpu stack into a module that otherwise runs headless.
    // Registered per construction so each match's fresh predictor replaces the
    // previous world without anyone remembering to rewire it.
    globalThis.__chaosPredictedWorld = () => this.world;
  }

  /**
   * Applies a mutation the moment its `chaos_event` lands, instead of waiting
   * up to a snapshot for `syncChaos` to catch it. Announce-phase events carry
   * the payload too (for HUD preview) but must not touch physics — the director
   * can still suppress a warned event before it lands.
   *
   * Ambient kinds pass through `applyMutation`'s own no-op; a malformed payload
   * is dropped rather than thrown, because a bad message from the server is not
   * worth crashing the render loop over — the snapshot ledger self-heals it.
   */
  applyChaosEvent(ev) {
    if (ev?.phase !== 'apply' || !isMutation(ev.mutation)) return;
    applyMutation(this.world, ev.mutation);
    if (Number.isInteger(ev.tick)) {
      const entry = this.world.chaos.applied.at(-1);
      if (entry && entry.mutation === ev.mutation) {
        const duration = entry.untilTick - entry.tick;
        entry.tick = ev.tick;
        entry.untilTick = entry.tick + duration;
      }
    }
  }

  /**
   * Converges the local chaos ledger onto the snapshot's authoritative one.
   *
   * Entries are matched by kind and overlapping window: an `applyChaosEvent`
   * landing computed its window from the local tick, which can sit a frame off
   * the server's, so an overlapping same-kind entry is ADOPTED into the
   * server's exact window rather than duplicated. Expired local entries are
   * left alone — `isActive` windows make them inert, and the reconciler replays
   * recent ticks whose physics they may still govern.
   */
  syncChaos(entries) {
    if (!entries) return;
    const applied = this.world.chaos.applied;
    const authoritativeHft = entries.filter((e) => e?.kind === 'hft_belt_flow' && isMutation(e.mutation));
    if (authoritativeHft.length > 0) {
      const first = applied.findIndex((entry) => entry.kind === 'hft_belt_flow');
      const insertAt = first < 0 ? applied.length : first;
      for (let i = applied.length - 1; i >= 0; i--) {
        if (applied[i].kind === 'hft_belt_flow') applied.splice(i, 1);
      }
      applied.splice(insertAt, 0, ...authoritativeHft.map((e) => ({
        kind: e.kind, tick: e.tick, untilTick: e.untilTick, mutation: e.mutation,
      })));
    }
    for (const e of entries) {
      if (!e || !isMutation(e.mutation)) continue;
      if (e.kind === 'hft_belt_flow') continue;
      let found = false;
      for (const a of applied) {
        if (a.kind !== e.kind) continue;
        if (a.tick < e.untilTick && e.tick < a.untilTick) {
          a.tick = e.tick;
          a.untilTick = e.untilTick;
          a.mutation = e.mutation;
          found = true;
          break;
        }
      }
      if (!found) {
        applied.push({ kind: e.kind, tick: e.tick, untilTick: e.untilTick, mutation: e.mutation });
      }
    }
  }

  /**
   * Takes the other players from a snapshot into this world as bodies.
   *
   * Decoys are not bodies: the sim never collides a player with a decoy, so
   * a decoy here would make the local body meet something the server's does
   * not. A player missing from the snapshot (left, or faded invisible) is
   * removed: an invisible opponent is absent from the bytes on purpose, and
   * the server's separation does still push the two apart — that one case is
   * a mispredict the visibility rule is worth.
   */
  syncBodies(snapshot) {
    // A snapshot with no roster (a partial one from a test rig, or a malformed
    // message) says nothing about who is where: the bodies stay as they were.
    if (!Array.isArray(snapshot?.players)) return;
    const seen = new Set();
    for (const s of snapshot.players) {
      if (s.id === this.playerId || typeof s.id !== 'string' || s.id.startsWith(DECOY_ID_PREFIX)) continue;
      seen.add(s.id);
      let r = this._remote.get(s.id);
      if (!r) {
        r = { player: this._insertBody(s.id, s.characterId), x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, tick: 0, phase: 'alive' };
        this._remote.set(s.id, r);
      }
      r.x = s.x;
      r.y = s.y;
      r.z = s.z;
      r.vx = s.vx ?? 0;
      r.vy = s.vy ?? 0;
      r.vz = s.vz ?? 0;
      r.tick = snapshot.tick;
      r.phase = s.phase;
    }
    for (const id of [...this._remote.keys()]) {
      if (!seen.has(id)) this._removeBody(id);
    }
    this.placeBodies();
  }

  /** The bodies this world holds for other players, for the harness and the debug overlay. */
  remoteBodies() {
    return this._remote;
  }

  _insertBody(id, characterId) {
    const character = CHARACTERS[characterId] ?? CHARACTERS.volt;
    const body = {
      id,
      characterId: character.id,
      x: 0,
      y: 0,
      z: 0,
      vx: 0,
      vy: 0,
      vz: 0,
      yaw: 0,
      health: REMOTE_HEALTH,
      phase: 'alive',
      cooldowns: {},
      ultCharge: 0,
      ammo: character.abilities.basic.ammo?.max ?? 0,
      reloadTicks: 0,
      effects: [],
      damageDealt: 0,
      kills: 0,
      placement: null,
      grounded: true,
      joinOrder: 0,
    };
    // `world.players` is sorted by id and every loop in the sim depends on
    // it: inserted in order, never appended.
    const players = this.world.players;
    let at = players.length;
    for (let i = 0; i < players.length; i++) {
      if (players[i].id > id) {
        at = i;
        break;
      }
    }
    players.splice(at, 0, body);
    this.world.aux[id] = createAux();
    return body;
  }

  _removeBody(id) {
    const i = this.world.players.findIndex((p) => p.id === id);
    if (i >= 0) this.world.players.splice(i, 1);
    delete this.world.aux[id];
    this._remote.delete(id);
  }

  /**
   * Puts every remote body where the server most likely has it for the tick
   * about to be stepped: its snapshot pose carried forward by its own velocity
   * for the ticks this world is ahead, capped. Re-placed before EVERY step —
   * so whatever the sim did to the body last step (separation's half-push,
   * a tick of gravity) is undone, and only the local body keeps what it was
   * pushed by. Zero velocity, so the step itself does not move it again.
   */
  placeBodies() {
    if (this._remote.size === 0) return;
    const next = this.world.tick + 1;
    for (const r of this._remote.values()) {
      const lead = Math.max(0, Math.min(REMOTE_LEAD_MAX_TICKS, next - r.tick));
      const p = r.player;
      p.x = r.x + r.vx * DT * lead;
      p.y = r.y + r.vy * DT * lead;
      p.z = r.z + r.vz * DT * lead;
      p.vx = 0;
      p.vy = 0;
      p.vz = 0;
      p.phase = r.phase;
      p.health = REMOTE_HEALTH;
      if (p.effects.length > 0) p.effects.length = 0;
    }
  }

  /**
   * Advances the local world by one tick with one input frame.
   *
   * Returns the events the local sim produced. They are used ONLY for immediate
   * self-feedback (a cast wind-up, a dash whoosh) — never for damage, never for
   * a kill, never for anything another player can see.
   */
  step(frame) {
    this.prev.x = this.player.x;
    this.prev.y = this.player.y;
    this.prev.z = this.player.z;
    this.prev.yaw = this.player.yaw;

    // The correction offset ages one tick per step, mirroring prev/curr, so
    // interpolate() bleeds it off continuously instead of in 30Hz stair-steps.
    // Replay steps decay it too, harmlessly: a reconcile that needs smoothing
    // overwrites both halves right after its replay.
    this._smoothPrev.x = this._smoothCurr.x;
    this._smoothPrev.y = this._smoothCurr.y;
    this._smoothPrev.z = this._smoothCurr.z;
    this._smoothCurr.x *= CORRECTION_DECAY;
    this._smoothCurr.y *= CORRECTION_DECAY;
    this._smoothCurr.z *= CORRECTION_DECAY;
    const s = this._smoothCurr;
    if (Math.abs(s.x) + Math.abs(s.y) + Math.abs(s.z) < CORRECTION_FLOOR) {
      s.x = s.y = s.z = 0;
    }

    this.placeBodies();
    this._inputs.clear();
    this._inputs.set(this.playerId, frame);

    // `stepWorld` refuses to advance a finished world. A client whose clock
    // drifted past `matchEndTick` would silently stop moving, so the flag is
    // cleared here — the server, not this world, decides when the match is over.
    this.world.ended = false;
    const events = stepWorld(this.world, this._inputs);

    this.restoreAuthoritative();

    this.curr.x = this.player.x;
    this.curr.y = this.player.y;
    this.curr.z = this.player.z;
    this.curr.yaw = this.player.yaw;
    return events;
  }

  /**
   * Re-asserts server truth over anything the local step invented.
   *
   * Without this a client that walks into the (locally wrong) zone predicts
   * itself to death, `phase` flips to "dead", and movement prediction stops for
   * a player the server still considers alive.
   */
  restoreAuthoritative() {
    this.player.health = this._auth.health;
    this.player.phase = this._auth.phase;
    this.player.ultCharge = this._auth.ultCharge;
    this.world.ended = false;
    this.world.winnerId = null;
    this.world.endReason = null;
    // Lethal geometry (the void, lava) can still push a death record before we
    // get here. Clearing it keeps the local world from accumulating deaths that
    // never happened, which would otherwise grow without bound over four minutes.
    if (this.world.deaths.length > 0) this.world.deaths.length = 0;
  }

  /**
   * Places the body at its committed spawn.
   *
   * The spawn comes from `world_init` — it is part of the seed commitment, so
   * this is the server's number, not a local guess. Without it the local world's
   * own seeded shuffle would put the player somewhere else for the whole drop.
   */
  setSpawn(x, y, z, yaw = 0) {
    this.player.x = x;
    this.player.y = y;
    this.player.z = z;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.vz = 0;
    this.player.yaw = yaw;
    this.prev = { x, y, z, yaw };
    this.curr = { x, y, z, yaw };
    // A spawn is a real teleport. Smoothing across it would draw the body
    // sliding from wherever the lobby camera left it.
    this.clearCorrectionSmoothing();
    this._lastRender = null;
  }

  /**
   * Where the body was last DRAWN — `interpolate`'s most recent output, offset
   * included. The reconciler captures this before a snap so the smoothing
   * offset starts from the exact on-screen position, not from a tick-boundary
   * approximation of it.
   */
  captureVisual(out) {
    const src = this._lastRender;
    if (src) {
      out.x = src.x;
      out.y = src.y;
      out.z = src.z;
    } else {
      out.x = this.curr.x + this._smoothCurr.x;
      out.y = this.curr.y + this._smoothCurr.y;
      out.z = this.curr.z + this._smoothCurr.z;
    }
    return out;
  }

  /**
   * Starts bleeding a correction off. Both halves get the full offset so the
   * first frame after the snap renders exactly where the previous frame did —
   * zero visible jump — and the decay begins on the next step.
   */
  beginCorrectionSmoothing(dx, dy, dz) {
    this._smoothPrev.x = this._smoothCurr.x = dx;
    this._smoothPrev.y = this._smoothCurr.y = dy;
    this._smoothPrev.z = this._smoothCurr.z = dz;
  }

  clearCorrectionSmoothing() {
    this._smoothPrev.x = this._smoothPrev.y = this._smoothPrev.z = 0;
    this._smoothCurr.x = this._smoothCurr.y = this._smoothCurr.z = 0;
  }

  /**
   * Folds an authoritative player snapshot into the local world.
   *
   * `snapPosition` false means only the non-positional fields are taken. The
   * reconciler now always takes the full restore and replays the unacked
   * inputs on top of it (see reconcile.js), which is what makes the effects
   * and casts below safe to take: an effect the replay's own inputs would
   * have applied is applied again by the replay.
   *
   * The magazine counts as positional here: the snapshot's count is for the
   * ACKED seq and knows nothing of presses still in flight, so it is only
   * right when the unacked presses are about to be re-spent on top of it.
   */
  applySnapshot(snap, { snapPosition }) {
    this._auth.health = snap.health;
    this._auth.phase = snap.phase;
    this._auth.ultCharge = snap.ultCharge;

    this.player.health = snap.health;
    this.player.phase = snap.phase;
    this.player.ultCharge = snap.ultCharge;
    // Cooldowns are absolute server ticks, so they are only meaningful next to a
    // server-aligned `world.tick`. Both are taken together, always.
    this.player.cooldowns = { ...snap.cooldowns };

    if (snapPosition) {
      this.player.ammo = snap.ammo;
      this.player.reloadTicks = reloadTicksFrom(this.player, snap.reload);
      this.player.x = snap.x;
      this.player.y = snap.y;
      this.player.z = snap.z;
      this.player.vx = snap.vx;
      this.player.vy = snap.vy;
      this.player.vz = snap.vz;
      this.player.yaw = snap.yaw;
      // What the server holds on you — every effect with its numbers, and
      // every cast you are inside — re-based on the tick this world was just
      // aligned to. A server that predates the fields sends neither, and the
      // local guess stands, which is what it did before.
      if (Array.isArray(snap.fx)) this._restoreEffects(snap.fx);
      if (Array.isArray(snap.casts)) this._restoreCasts(snap.casts);
      if (snap.buf) this._restoreBuffers(snap.buf);
    }
  }

  _restoreEffects(fx) {
    const tick = this.world.tick;
    const effects = this.player.effects;
    effects.length = 0;
    for (const e of fx) {
      if (!e || typeof e.kind !== 'string' || !Number.isFinite(e.left)) continue;
      effects.push({
        kind: e.kind,
        untilTick: tick + e.left,
        magnitude: Number.isFinite(e.magnitude) ? e.magnitude : 0,
        // Who applied it credits damage, which this world never deals.
        sourceId: '',
        ax: Number.isFinite(e.ax) ? e.ax : 0,
        ay: Number.isFinite(e.ay) ? e.ay : 0,
        az: Number.isFinite(e.az) ? e.az : 0,
        ticks: Number.isFinite(e.ticks) ? e.ticks : 0,
      });
    }
    // The travel exit edge lives in aux: a body restored mid-travel was
    // travelling last tick as far as the exit rule is concerned.
    const aux = this.world.aux[this.playerId];
    const travel = findTravel(this.player);
    if (aux) {
      aux.wasTravelling = travel !== null;
      aux.wasFlight = travel !== null && isFlight(travel.kind);
    }
  }

  /** The presses the server is holding for you (see `InputBufferSnapshot`). */
  _restoreBuffers(buf) {
    const aux = this.world.aux[this.playerId];
    if (!aux) return;
    aux.dashBufferTicks = Number.isFinite(buf.dash) ? buf.dash : 0;
    aux.castBufferTicks = Number.isFinite(buf.cast) ? buf.cast : 0;
    aux.castBufferSlot = typeof buf.castSlot === 'string' ? buf.castSlot : null;
    // The dash exit is edge-triggered on this flag, like the travel exit.
    aux.wasDashing = this.player.effects.some((e) => e.kind === 'dash');
  }

  _restoreCasts(casts) {
    const tick = this.world.tick;
    const mine = [];
    for (const c of casts) {
      if (!c || typeof c.abilityId !== 'string' || !Number.isFinite(c.left)) continue;
      mine.push({
        playerId: this.playerId,
        abilityId: c.abilityId,
        slot: c.slot,
        resolveTick: tick + c.left,
        yaw: c.yaw ?? 0,
        x: c.x ?? this.player.x,
        y: c.y ?? this.player.y,
        z: c.z ?? this.player.z,
        pellet: Number.isFinite(c.pellet) ? c.pellet : -1,
        stage: c.stage === 'arrive' ? 'arrive' : 'strike',
      });
    }
    this.world.casts = this.world.casts.filter((c) => c.playerId !== this.playerId).concat(mine);
  }

  /** Aligns the local clock with the server's. Cooldown maths depends on it. */
  setTick(tick) {
    this.world.tick = tick;
  }

  /** 0..1 toward the next pip, from the predicted magazine, for the HUD. */
  reloadFraction() {
    return reloadFraction(this.player);
  }

  /** Distance between the predicted body and an authoritative position. */
  errorFrom(snap) {
    const dx = this.player.x - snap.x;
    const dy = this.player.y - snap.y;
    const dz = this.player.z - snap.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Render position for a partial tick. `alpha` is 0..1 within the tick.
   *
   * The correction offset rides on top of the authoritative lerp — this is the
   * ONLY place it is ever added, which is what makes the smoothing render-only:
   * ability origins, hit feedback and the debug overlay all read `this.player`
   * and see the snapped truth.
   */
  interpolate(alpha, out) {
    out.set(
      this.prev.x + (this.curr.x - this.prev.x) * alpha +
        this._smoothPrev.x + (this._smoothCurr.x - this._smoothPrev.x) * alpha,
      this.prev.y + (this.curr.y - this.prev.y) * alpha +
        this._smoothPrev.y + (this._smoothCurr.y - this._smoothPrev.y) * alpha,
      this.prev.z + (this.curr.z - this.prev.z) * alpha +
        this._smoothPrev.z + (this._smoothCurr.z - this._smoothPrev.z) * alpha
    );
    if (!this._lastRender) this._lastRender = { x: 0, y: 0, z: 0 };
    this._lastRender.x = out.x;
    this._lastRender.y = out.y;
    this._lastRender.z = out.z;
    return out;
  }
}
